<script lang="ts" setup>
import {
  PhCalendarBlank,
  PhCircleNotch,
  PhClipboardText,
  PhFloppyDisk,
  PhPencilSimple,
  PhPlus,
  PhTrash,
  PhX,
} from '@phosphor-icons/vue';
import { useI18n } from 'vue-i18n';
import {
  useAfterMarketTableLogic,
  type UseAfterMarketTableLogicProps,
} from './logic/useAfterMarketTableLogic';
import type { AfterMarketRow, AfterMarketSaveCallbacks } from './types';

const props = defineProps<UseAfterMarketTableLogicProps>();
const emit = defineEmits<{
  save: [rows: AfterMarketRow[], callbacks: AfterMarketSaveCallbacks];
}>();
const { t } = useI18n();
const { state, action } = useAfterMarketTableLogic(props, emit);
</script>

<template>
  <div class="card">
    <div class="card-header">
      <p class="card-header-title">
        <PhClipboardText :size="18" class="mr-2" />
        {{ t('overview.afterMarketTable.title') }}
      </p>
      <button
        v-if="state.isEditing"
        class="header-btn cancel-btn"
        @click="action.cancel()"
      >
        <PhX :size="15" />
        {{ t('cancel') }}
      </button>
      <button
        v-if="state.mode !== 'read-only'"
        class="header-btn edit-btn"
        :class="{ 'is-active': state.isEditing }"
        :disabled="state.isSaving"
        @click="state.isEditing ? action.requestSave() : action.startEdit()"
      >
        <PhFloppyDisk v-if="state.isEditing" :size="15" />
        <PhCircleNotch v-else-if="state.isSaving" :size="15" class="spin" />
        <PhPencilSimple v-else :size="15" />
        {{
          state.isEditing || state.isSaving
            ? t('save')
            : t('overview.afterMarketTable.edit')
        }}
      </button>
    </div>

    <div class="card-content p-0">
      <table class="table is-fullwidth is-striped is-hoverable mb-0">
        <thead>
          <tr>
            <th>{{ t('overview.afterMarketTable.table.visit') }}</th>
            <th class="date-cell">
              {{ t('overview.afterMarketTable.table.date') }}
            </th>
            <th v-if="state.isEditing" class="col-action"></th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-if="state.sortedRows.length === 0"
            class="empty-state-row"
          >
            <td :colspan="state.isEditing ? 3 : 2">
              <div class="empty-state-content">
                <PhClipboardText :size="22" class="empty-state-icon" />
                {{ t('overview.afterMarketTable.emptyState') }}
              </div>
            </td>
          </tr>
          <tr v-for="row in state.sortedRows" :key="row.id">
            <td class="col-text">
              <template v-if="state.isEditing">
                <span>
                  <input
                    class="inline-input text-input"
                    type="text"
                    :placeholder="t('overview.afterMarketTable.table.visit')"
                    :value="row.text"
                    @change="
                      action.updateRow(
                        row.id,
                        'text',
                        ($event.target as HTMLInputElement).value,
                      )
                    "
                  />
                </span>
              </template>
              <template v-else>{{ row.text || '–' }}</template>
            </td>
            <td class="date-cell">
              <template v-if="state.isEditing">
                <span class="date-edit-field">
                  <input
                    class="inline-input date-input"
                    type="text"
                    inputmode="numeric"
                    :placeholder="
                      t('overview.afterMarketTable.datePlaceholder')
                    "
                    :value="row.date"
                    :size="row.date.length || 10"
                    @change="
                      action.updateRow(
                        row.id,
                        'date',
                        ($event.target as HTMLInputElement).value,
                      )
                    "
                  />
                  <span class="date-picker-icon">
                    <PhCalendarBlank :size="14" />
                    <input
                      type="date"
                      class="date-picker-overlay"
                      :value="row.date"
                      @change="
                        action.updateRow(
                          row.id,
                          'date',
                          ($event.target as HTMLInputElement).value,
                        )
                      "
                    />
                  </span>
                </span>
              </template>
              <template v-else
                ><span>{{ row.date || '–' }}</span></template
              >
            </td>
            <td v-if="state.isEditing" class="col-action">
              <button class="row-delete-btn" @click="action.deleteRow(row.id)">
                <PhTrash :size="13" />
              </button>
            </td>
          </tr>
        </tbody>
        <tfoot v-if="state.isEditing">
          <tr class="add-row-tr">
            <td class="add-row-td" colspan="3">
              <div class="add-row-content">
                <div class="new-entry-pill">
                  <input
                    class="pill-input pill-text-input"
                    type="text"
                    :placeholder="t('overview.afterMarketTable.table.visit')"
                    :value="state.newRowDraft.text"
                    @input="
                      action.updateNewRow(
                        'text',
                        ($event.target as HTMLInputElement).value,
                      )
                    "
                  />
                  <span class="pill-divider" />
                  <span class="date-edit-field">
                    <input
                      class="pill-input date-input"
                      type="text"
                      inputmode="numeric"
                      :placeholder="
                        t('overview.afterMarketTable.datePlaceholder')
                      "
                      :value="state.newRowDraft.date"
                      :size="state.newRowDraft.date.length || 10"
                      @change="
                        action.updateNewRow(
                          'date',
                          ($event.target as HTMLInputElement).value,
                        )
                      "
                    />
                    <span class="date-picker-icon">
                      <PhCalendarBlank :size="14" />
                      <input
                        type="date"
                        class="date-picker-overlay"
                        :value="state.newRowDraft.date"
                        @change="
                          action.updateNewRow(
                            'date',
                            ($event.target as HTMLInputElement).value,
                          )
                        "
                      />
                    </span>
                  </span>
                </div>
                <button class="add-row-btn" @click="action.addRow()">
                  <PhPlus :size="14" />
                  {{ t('overview.afterMarketTable.addVisit') }}
                </button>
              </div>
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  </div>
</template>

<style scoped>
.card {
  overflow: hidden;
  display: flex;
  flex-direction: column;
  max-height: 30rem;
}

.card-content {
  overflow-y: auto;
  flex: 1;
  min-height: 0;
}

.card-header {
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  position: relative;
  z-index: 2;
}

.header-btn {
  margin-right: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.3rem 0.75rem;
  align-self: center;
  border: 1px solid #e0e0e0;
  border-radius: 999px;
  background: #f5f5f7;
  font-size: 0.75rem;
  font-weight: 600;
  color: #666;
  cursor: pointer;
  transition:
    background 0.15s,
    color 0.15s,
    border-color 0.15s;
}

.edit-btn.is-active {
  background: var(--primary-color);
  border-color: var(--primary-color);
  color: #fff;
}

.cancel-btn {
  margin-right: 0.4rem;
}

.cancel-btn:hover {
  background: #fee2e2;
  border-color: #fca5a5;
  color: #dc2626;
}

.table thead th {
  position: sticky;
  top: 0;
  z-index: 1;
  background: #fff;
  box-shadow: inset 0 -2px 0 #dbdbdb;
  border-bottom: none;
}

.table th,
.table td {
  padding: 0.6rem 0.85rem;
  vertical-align: middle;
  line-height: 1.5;
}

.table tbody tr {
  height: 2.7rem;
}

.col-text {
  width: 65%;
}

.date-cell {
  width: 35%;
  white-space: nowrap;
  text-align: right !important;
}

.col-action {
  width: 3rem;
  text-align: right;
  vertical-align: middle;
  line-height: 0;
}

.empty-state-row td {
  color: #aaa;
  font-style: italic;
  font-weight: 400 !important;
  text-align: center;
  padding-top: 2.5rem;
  padding-bottom: 2.5rem;
}

.empty-state-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
}

.empty-state-icon {
  opacity: 0.4;
}

.inline-input {
  background: transparent;
  border: none;
  box-shadow: inset 0 -1px 0 #aaa;
  outline: none;
  padding: 0;
  margin: 0;
  min-height: 0;
  height: 1.5em;
  vertical-align: top;
  font-family: inherit;
  font-size: inherit;
  font-weight: inherit;
  line-height: 1.5;
  box-sizing: border-box;
  color: inherit;
  appearance: none;
  -webkit-appearance: none;
  transition: box-shadow 0.15s;
}

.inline-input:hover {
  box-shadow: inset 0 -1px 0 #666;
}

.inline-input:focus {
  box-shadow: inset 0 -1px 0 var(--primary-color);
}

.inline-input::placeholder {
  color: #bbb;
}

.text-input {
  width: 100%;
}

.date-input {
  text-align: right;
}

.date-edit-field {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  vertical-align: top;
}

.date-picker-icon {
  position: relative;
  display: inline-flex;
  align-items: center;
  color: #555;
  cursor: pointer;
}

.date-picker-icon:hover {
  color: var(--primary-color);
}

.date-picker-overlay {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
  padding: 0;
  margin: 0;
  border: none;
}

.row-delete-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.5em;
  height: 1.5em;
  padding: 0;
  margin: 0;
  box-sizing: border-box;
  border: none;
  border-radius: 50%;
  vertical-align: middle;
  background: #fee2e2;
  color: #dc2626;
  cursor: pointer;
  transition: background 0.15s;
}

.row-delete-btn:hover {
  background: #fca5a5;
}

.add-row-tr td.add-row-td {
  background: color-mix(in srgb, var(--primary-color) 5%, white) !important;
  box-shadow: inset 0 2px 0 color-mix(in srgb, var(--primary-color) 40%, white);
  border-top: none !important;
  position: sticky;
  bottom: 0;
  padding-top: 0.85rem;
  padding-bottom: 0.85rem;
}

.add-row-content {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.new-entry-pill {
  display: flex;
  align-items: center;
  flex: 1;
  background: #fff;
  border-radius: 999px;
  padding: 0.45rem 0.85rem;
  gap: 0.5rem;
}

.pill-input {
  background: transparent;
  border: none;
  outline: none;
  padding: 0;
  margin: 0;
  font-family: inherit;
  font-size: inherit;
  color: inherit;
  line-height: 1.5;
}

.pill-input::placeholder {
  color: #bbb;
}

.pill-text-input {
  flex: 1;
  min-width: 0;
}

.pill-divider {
  width: 1px;
  height: 1em;
  background: #ddd;
  flex-shrink: 0;
}

.date-btn-row {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.75rem;
}

.add-row-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  white-space: nowrap;
  flex-shrink: 0;
  padding: 0.3rem 0.75rem;
  border: none;
  border-radius: 999px;
  background: var(--primary-color);
  font-size: 0.75rem;
  font-weight: 600;
  color: #fff;
  cursor: pointer;
  transition: opacity 0.15s;
}

.add-row-btn:hover {
  opacity: 0.85;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.spin {
  animation: spin 0.8s linear infinite;
}
</style>
